import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';

import { Container } from '@Calix-Commerce/design-system/layout';
import { AccountBlock } from '@Calix-Commerce/design-system/components';

import { authenticationState, searchState, loadingState, currentAccountState } from 'state/atoms';
import { SEARCH_ORDERS } from 'connectors/queries/order';

import { Search, ResultsTable } from './subcomponents';
import styled from '@emotion/styled';

const CurrencyCode = styled.h3`
  font-size: 1.5rem;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.primary};
  text-transform: uppercase;
`;

const Orders = () => {
  const { endCustomerAccountId, fullName, account } = useRecoilValue(authenticationState);
  const [searchParams, setSearchParams] = useRecoilState(searchState);
  const setLoading = useSetRecoilState(loadingState);
  const { defaultCurrencyCode } = useRecoilValue(currentAccountState);

  const isFirstSearch = useRef(true);

  const [requestError, setRequestError] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);

  const [searchOrders, { data }] = useLazyQuery(SEARCH_ORDERS, {
    fetchPolicy: isFirstSearch.current ? 'cache-only' : 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setInternalLoading(false);
    },
    onError: (error) => {
      setRequestError(error);
      setInternalLoading(false);
    },
  });

  const runSearch = () => {
    const params = {
      ...searchParams,
      searchTerm: searchParams.searchTerm.trim(),
    };
    setSearchParams(params); //Update search state with trimmed param

    searchOrders({
      variables: {
        ...params,
        accountId: endCustomerAccountId,
      },
    });
  };

  useEffect(() => {
    setLoading(false);
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchSubmit = () => {
    if (!isFirstSearch.current) {
      setRequestError(null);
    }

    // Set isFirstSearch to false in order to run the submit query as network-only
    isFirstSearch.current = false;
    setInternalLoading(true);
    runSearch();
  };

  return (
    <Container>
      <AccountBlock appName="Order Manager" userName={fullName} accountName={account} />
      <CurrencyCode>ACCOUNT CURRENCY: {defaultCurrencyCode}</CurrencyCode>
      <Search onSubmit={onSearchSubmit} loading={internalLoading} />
      <ResultsTable
        orderData={internalLoading ? null : data}
        loading={internalLoading}
        error={requestError}
        hasSearched={!isFirstSearch.current}
      />
    </Container>
  );
};

export { Orders };
